import { useEffect, useState } from "react";
import type { ReactElement } from "react";
import type { AnsprechpartnerKategorie, ApiClient, Unternehmen } from "@kundeninfo/api-client";
import { darfAbbrechen, Feld, Modal, ModalFuss, useBestaetigung, useFormularZustand } from "@kundeninfo/ui";
import { Auswahl, Chip, Formular, Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { KEINE_ZAHL, Meldung, alsZahl, eingabeUnlesbar, meldungAus } from "./gemeinsam.js";
import { KiKnopf } from "../../components/meHuellen.js";
import { CATEGORY_TEXT as T } from "./representativeAdminTexts.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich ANSPRECHPARTNER-BEREICHE (Migration 069): der Katalog der Paare an Angebot und Auftrag. Bauart
 * wie die Ansprechpartner-Rollen (ohne zweite Ebene — alle Felder stehen in der Zeile), dazu „Gilt für“ (übergreifend
 * oder ein Unternehmen, nur beim Anlegen), Archivieren und Löschen ohne Verwendung.
 */
export type KategorienClient = Pick<ApiClient, "listAnsprechpartnerKategorien" | "createAnsprechpartnerKategorie"
  | "updateAnsprechpartnerKategorie" | "deleteAnsprechpartnerKategorie">;

type Entwurf = { id: string | null; version: number; companyId: string; name: string; sortIndex: string; archived: boolean };
const FELDER = ["name", "sortIndex", "companyId"] as const;

const dialogAus = (k: AnsprechpartnerKategorie | null): Entwurf => k === null
  ? { id: null, version: 0, companyId: "", name: "", sortIndex: "0", archived: false }
  : { id: k.id, version: k.version, companyId: k.companyId ?? "", name: k.name, sortIndex: String(k.sortIndex),
      archived: k.archived };

export function KategorienBereich(p: { client: KategorienClient; companies: readonly Unternehmen[] }): ReactElement {
  const { client } = p;
  const [zeilen, setZeilen] = useState<AnsprechpartnerKategorie[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Entwurf | null>(null);
  const [auskunft, setAuskunft] = useState<string | null>(null);
  const { bestaetigen } = useBestaetigung();
  const list = client.listAnsprechpartnerKategorien;

  const neuLesen = async (): Promise<AnsprechpartnerKategorie[]> => {
    const a = await list();
    setZeilen(a.items);
    return a.items;
  };
  useEffect(() => {
    let ab = false;
    list().then((a) => { if (!ab) setZeilen(a.items); },
      (e: unknown) => { if (!ab) setLadeFehler(meldungAus(e, T.loadFailed)); });
    return () => { ab = true; };
  }, [list]);

  const formular = useFormularZustand<true>({
    speichern: async () => {
      const d = dialog;
      if (d === null) throw new Error("Kein Bereich offen.");
      const sortIndex = alsZahl(d.sortIndex);
      if (sortIndex === KEINE_ZAHL || sortIndex === null) {
        throw eingabeUnlesbar([{ field: "sortIndex", message: T.numberMissing }]);
      }
      if (d.id === null) {
        await client.createAnsprechpartnerKategorie({ companyId: d.companyId === "" ? null : d.companyId, name: d.name,
                                                      sortIndex });
      } else {
        await client.updateAnsprechpartnerKategorie(d.id, { version: d.version, name: d.name, sortIndex,
                                                            archived: d.archived });
      }
      await neuLesen();
      return true;
    },
    neuLaden: async () => {
      const frisch = await neuLesen();
      const d = dialog;
      if (d === null || d.id === null) return;
      const k = frisch.find((z) => z.id === d.id);
      if (k === undefined) { setDialog(null); setAuskunft(T.gone); return; }
      setDialog(dialogAus(k));
    },
    bekannteFelder: FELDER,
    beschriftungen: { name: T.name, sortIndex: T.sortIndex, companyId: T.company },
  });
  const arbeitet = formular.laeuft;
  const feldFehler = formular.feldFehler as Partial<Record<(typeof FELDER)[number], string>>;
  const geaendert = dialog !== null && JSON.stringify(dialog)
    !== JSON.stringify(dialogAus(zeilen?.find((z) => z.id === dialog.id) ?? null));

  const firma = (id: string | null): string => (id === null ? T.global
    : p.companies.find((c) => c.id === id)?.shortCode ?? id);

  async function abbrechen(): Promise<void> {
    if (arbeitet || !(await darfAbbrechen(geaendert))) return;
    setDialog(null);
  }
  async function speichern(): Promise<void> {
    if (dialog === null) return;
    if (!(await bestaetigen({ titel: T.confirmSaveTitle, text: T.confirmSaveText(dialog.name.trim()),
                              bestaetigenText: T.save }))) return;
    if (await formular.aktion() === true) setDialog(null);
  }
  async function loeschen(k: AnsprechpartnerKategorie): Promise<void> {
    if (!(await bestaetigen({ titel: T.confirmRemoveTitle, text: T.confirmRemoveText(k.name),
                              bestaetigenText: T.removeShort, gefaehrlich: true }))) return;
    try {
      await client.deleteAnsprechpartnerKategorie(k.id);
      await neuLesen();
    } catch (e) {
      setAuskunft(meldungAus(e, T.loadFailed));
      await neuLesen();
    }
  }
  const oeffnen = (k: AnsprechpartnerKategorie | null): void => {
    formular.zuruecksetzen();
    setAuskunft(null);
    setDialog(dialogAus(k));
  };
  const setze = (teil: Partial<Entwurf>): void => setDialog((d) => (d === null ? d : { ...d, ...teil }));

  if (ladeFehler !== null) return <Meldung text={ladeFehler} />;
  if (zeilen === null) return <p className="vw__hinweis">{T.loading}</p>;
  return (
    <>
      <p className="vw__hinweis">{T.hint}</p>
      {auskunft !== null && <Hinweis art="info">{auskunft}</Hinweis>}
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => oeffnen(null)}>{T.new}</KiKnopf>
      </div>
      <ul className="vw__liste">
        {zeilen.map((k) => (
          <li key={k.id} className="vw__eintrag">
            <div className="vw__eintrag-kopf">
              <span className="vw__eintrag-name">{k.name}</span>
              <Chip className="vw__marke">{firma(k.companyId)}</Chip>
              {k.archived && <Chip className="vw__marke">{T.archivedChip}</Chip>}
            </div>
            <span className="vw__eintrag-zusatz">
              {T.order(k.sortIndex)}{k.usage > 0 ? ` · ${T.usage(k.usage)}` : ""}
            </span>
            <div className="vw__eintrag-aktionen">
              <KiKnopf name={T.change(k.name)} onClick={() => oeffnen(k)}>{T.changeShort}</KiKnopf>
              {k.usage === 0 && (
                <KiKnopf name={T.remove(k.name)} onClick={() => { void loeschen(k); }}>{T.removeShort}</KiKnopf>
              )}
            </div>
          </li>
        ))}
      </ul>
      {dialog !== null && (
        <Modal titel={dialog.id === null ? T.titleNew : T.titleEdit} onAbbrechen={abbrechen} abbrechenGesperrt={arbeitet}>
          {formular.meldung !== null && <Meldung text={formular.meldung} />}
          <Formular beschriftung="oben" {...formular.formularProps}
            hinweis={formular.zustand === "fremder-stand" ? <span>{T.stale}</span> : undefined}>
            <Auswahl id="vw-ak-company" name="vw-ak-company" beschriftung={T.company} wert={dialog.companyId}
              gesperrt={dialog.id !== null} {...(feldFehler.companyId === undefined ? {} : { fehler: feldFehler.companyId })}
              onGeaendert={(d) => setze({ companyId: d.wert })}>
              <option value="">{T.global}</option>
              {p.companies.map((c) => <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>)}
            </Auswahl>
            <Feld id="vw-ak-name" label={T.name} bearbeitbar wert={dialog.name} onChange={(w) => setze({ name: w })}
              {...(feldFehler.name === undefined ? {} : { fehler: feldFehler.name })} />
            <Feld id="vw-ak-sortIndex" label={T.sortIndex} bearbeitbar wert={dialog.sortIndex}
              onChange={(w) => setze({ sortIndex: w })}
              {...(feldFehler.sortIndex === undefined ? {} : { fehler: feldFehler.sortIndex })} />
            {dialog.id !== null && (
              <Schalter id="vw-ak-archived" beschriftung={T.archived} an={dialog.archived}
                onGeaendert={(e) => setze({ archived: e.an })} />
            )}
          </Formular>
          <ModalFuss speichernText={T.save} speichernGesperrt={formular.zustand !== "bereit"}
            abbrechenGesperrt={arbeitet} onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}
    </>
  );
}
